import React, { useEffect, useState } from "react";
import { Mail, AlertTriangle, ShieldCheck, Pencil, Loader2 } from "lucide-react";
import api from "../../api";

// Resend headroom at a glance, plus a manual override so the admin can bump the plan/quota after
// upgrading Resend (default is now the paid 100k/month, no daily cap).
export const AdminEmailUsage = () => {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [plan, setPlan] = useState("pro");
  const [quota, setQuota] = useState(100000);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/email-usage").then((r) => { setD(r.data); setPlan(r.data.plan); setQuota(r.data.monthlyQuota); }).catch(() => {}); }, []);
  if (!d) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/email-usage/config", { plan, monthlyQuota: Number(quota) });
      setD(data); setPlan(data.plan); setQuota(data.monthlyQuota); setEditing(false);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const pct = Math.min(100, Math.round((d.sent / Math.max(1, d.quota)) * 100));
  const tone = d.quotaHits > 0 || pct >= 100 ? "#e11d48" : pct >= 75 ? "#f5b942" : "#16a34a";
  const peak = Math.max(1, ...d.history.map((h) => h.sent));

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-email-usage">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-bold text-foreground flex items-center gap-2">
          <Mail size={18} className="text-[#16a34a]" /> {d.period === "month" ? "Email this month" : "Email today"}
        </h2>
        <button onClick={() => setEditing((v) => !v)} data-testid="email-config-edit"
          className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <Pencil size={13} /> Edit plan
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">
        Sent from {d.sender} on the Resend {d.plan} plan
        {d.period === "month" ? ` — ${d.monthlyQuota.toLocaleString()} a month, no daily cap` : ` — ${d.quota.toLocaleString()} a day`}.
        {d.shared && " The allowance is shared with every other domain on that account, so the real headroom is lower than this count."}
      </p>

      {editing && (
        <div className="mb-4 rounded-xl border border-border bg-background p-3 space-y-2" data-testid="email-config-editor">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs font-semibold text-muted-foreground">Plan
              <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="email-config-plan"
                className="mt-1 block bg-card border border-border rounded-lg px-2 py-1.5 text-sm text-foreground">
                <option value="free">Free (daily cap)</option>
                <option value="pro">Pro</option>
                <option value="scale">Scale</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-muted-foreground">Monthly quota
              <input type="number" min="1" value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="email-config-quota"
                className="mt-1 block w-32 bg-card border border-border rounded-lg px-2 py-1.5 text-sm text-foreground" />
            </label>
            <button onClick={save} disabled={saving} data-testid="email-config-save"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#16a34a] hover:bg-[#15803d] rounded-lg px-3 py-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">Any plan other than Free removes the daily cap and counts against the monthly quota.</p>
        </div>
      )}

      <div className="flex items-end justify-between gap-3">
        <p className="text-3xl font-extrabold" style={{ color: tone }} data-testid="email-sent-count">
          {d.sent}<span className="text-base font-bold text-muted-foreground"> / {d.quota.toLocaleString()}</span>
        </p>
        <p className="text-xs text-muted-foreground" data-testid="email-remaining">
          {d.remaining.toLocaleString()} left {d.shared ? "at most " : ""}{d.period === "month" ? "this month" : "today"}
          {d.period === "month" ? ` · ${d.sentToday} today` : ""}
        </p>
      </div>
      <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: tone }} />
      </div>

      {(d.quotaHits > 0 || d.failed > 0) && (
        <p className="mt-3 text-xs font-semibold flex items-start gap-1.5" style={{ color: tone }} data-testid="email-quota-warning">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          {d.quotaHits > 0
            ? `${d.quotaHits} email${d.quotaHits === 1 ? " was" : "s were"} refused for hitting the Resend cap today — resets won't reach anyone until it clears. ${d.plan === "free" ? "Time to move off the free plan." : "Check the Resend usage page."}`
            : `${d.failed} email${d.failed === 1 ? "" : "s"} failed to send today.`}
        </p>
      )}

      {d.deferred > 0 && (
        <p className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5" data-testid="email-deferred-note">
          <ShieldCheck size={14} className="shrink-0 mt-0.5 text-[#16a34a]" />
          {d.deferred} bulk email{d.deferred === 1 ? "" : "s"} held back today to keep the last {d.reserve} for
          verification codes, password resets and 2FA. They go out once the allowance clears.
        </p>
      )}

      <div className="mt-4 flex items-end gap-1.5 h-16" data-testid="email-history">
        {d.history.map((h) => (
          <div key={h.day} className="flex-1 flex flex-col items-center gap-1" title={`${h.day}: ${h.sent} sent, ${h.failed + h.quota} failed`}>
            <div className="w-full rounded-t bg-[#16a34a]/70" style={{ height: `${Math.max(3, (h.sent / peak) * 100)}%` }} />
            <span className="text-[10px] text-muted-foreground">{h.day.slice(8)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminEmailUsage;
